import { orderBy } from 'lodash';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { PAGE_SIZE } from '~/common/constants/page';
import { selectWidths } from '~/common/constants/selectWidths';
import { Box } from '~/eds/src/components/Box/Box';
import { DepartmentSelect as EdsDepartmentSelect } from '~/eds/src/components/Select/subcomponents/DepartmentSelect';
import { PilotId as EdsPilotId } from '~/eds/src/types/PilotId';
import { SharedSelectProps } from '~/eds/src/types/SharedSelectProps';
import { withClient } from '~/hocs/withClient';
import { useDebouncedCallback } from '~/hooks/useDebouncedCallback';
import { api } from '~/redux/api/createApi';
import { coerceRtkqError } from '~/redux/api/utils';
import { DEPRECATED_Client } from '~/types/Client';
import { DepartmentV2, NormalizedDepartments } from '~/types/Department';
import { PilotId } from '~/types/PilotId';
import { normalizeDepartmentTree } from '~/utils/departments';

import { transfromDepartmentsToOptions } from '../UserDepartmentSelect/UserDepartmentSelect';
import { createCustomOption } from './utils';

type SelectWidths = 's' | 'm' | 'l' | 'fullWidth';

interface Props<M> extends Omit<SharedSelectProps<EdsPilotId, M>, 'width'> {
  /** Options that we want to hide */
  notAvailableDepartments?: EdsPilotId[];
  /** Options we want to retrict the user to */
  specificDepartments?: EdsPilotId[];
  /** Width of the country select. It defaults to 'm' */
  width?: SelectWidths;
  // connected
  client: DEPRECATED_Client;
  /** Fetch departments of a specific client instead of current client (mostly for super admin) */
  clientId?: PilotId;
}

const MIN_FETCHED_DEPARTMENTS = 5;

const DepartmentSelect = <M extends boolean>({
  notAvailableDepartments,
  client,
  clientId,
  error,
  isMulti,
  value,
  specificDepartments,
  width = 'm',
  onChange,
  name,
  ...rest
}: Props<M>) => {
  const intl = useIntl();
  const currentClientId = clientId ?? client.id;
  const { data: clientDepartmentsTree, isFetching: isFetchingDepartmentsTree } =
    api.endpoints.getClientDepartmentsTree.useQuery(currentClientId);

  const [findDepartments, departmentsResult] =
    api.endpoints.findDepartmentsByName.useLazyQuery();
  const { error: fetchDepartmentsError, isFetching: isFetchingDepartments } =
    departmentsResult;
  const initialSearchParams = useMemo(
    () =>
      currentClientId
        ? { clientId: currentClientId, pageSize: PAGE_SIZE, query: '' }
        : null,
    [currentClientId],
  );
  const debouncedFindDepartments = useDebouncedCallback(
    findDepartments,
    initialSearchParams,
  );
  const fetchedDepartments = departmentsResult?.data?.results || [];

  const departments: NormalizedDepartments = useMemo(() => {
    const isClientOverride = !!clientId;
    // use client's preloaded departmentTree until the updated one is queried
    // this way, preloaded values are shown right away and there is no render shift as the queried departments are loaded in
    // don't use fallback if using custom clientId
    const departmentTree =
      !isClientOverride && !clientDepartmentsTree
        ? client.departmentTree
        : clientDepartmentsTree;
    return normalizeDepartmentTree(
      departmentTree ?? [],
    ) as NormalizedDepartments;
  }, [clientDepartmentsTree, client.departmentTree]);

  const preloadedDepartments = useMemo(() => {
    const departmentOptions: DepartmentV2[] = [];
    if (Array.isArray(value)) {
      value?.forEach((val: EdsPilotId) => {
        const typedValue = val as number;
        if (departments[typedValue]) {
          departmentOptions.push(
            transfromDepartmentsToOptions(departments[typedValue], departments),
          );
        } else {
          departmentOptions.push(
            isFetchingDepartmentsTree
              ? createCustomOption(
                  typedValue,
                  intl.formatMessage({
                    id: 'DepartmentSelect.loadingValue',
                    defaultMessage: 'Loading value...',
                    description:
                      'Select option value for when component is fetching department information',
                  }),
                )
              : createCustomOption(
                  typedValue,
                  intl.formatMessage({
                    id: 'DepartmentSelect.removedDepartment',
                    defaultMessage: 'Removed department',
                    description:
                      'Select option value for when a department is deleted',
                  }),
                ),
          );
        }
      });
    } else {
      // if there is no value, we should skip
      if (value) {
        const typedValue = value as number;
        if (departments[typedValue]) {
          departmentOptions.push(
            transfromDepartmentsToOptions(departments[typedValue], departments),
          );
        } else {
          departmentOptions.push(
            isFetchingDepartmentsTree
              ? createCustomOption(
                  typedValue,
                  intl.formatMessage({
                    id: 'DepartmentSelect.loadingValue',
                    defaultMessage: 'Loading value...',
                    description:
                      'Select option value for when component is fetching department information',
                  }),
                )
              : createCustomOption(
                  typedValue,
                  intl.formatMessage({
                    id: 'DepartmentSelect.removedDepartment',
                    defaultMessage: 'Removed department',
                    description:
                      'Select option value for when a department is deleted',
                  }),
                ),
          );
        }
      }
    }

    if (specificDepartments?.length) {
      specificDepartments.forEach((depId: EdsPilotId) => {
        const typedDepId = depId as number;
        if (
          departments[typedDepId] &&
          !departmentOptions.find((department) => department.id === typedDepId)
        ) {
          departmentOptions.push(
            transfromDepartmentsToOptions(departments[typedDepId], departments),
          );
        }
      });
    }

    return departmentOptions;
  }, [value, departments, isFetchingDepartmentsTree]);

  const allDepartments = useMemo(() => {
    const preloadedIds = new Set(preloadedDepartments.map((dept) => dept.id));
    const fetchedOnly = fetchedDepartments.filter(
      (dept) => !preloadedIds.has(dept.id),
    );

    // Always include all preloaded departments
    // For fetched: if total would exceed PAGE_SIZE, limit fetched to max 5
    const baseSlotsFromPageSize = Math.max(
      0,
      PAGE_SIZE - preloadedDepartments.length,
    );
    const fetchedSlots =
      preloadedDepartments.length + baseSlotsFromPageSize > PAGE_SIZE
        ? MIN_FETCHED_DEPARTMENTS
        : baseSlotsFromPageSize;
    const finalDepartments = [
      ...preloadedDepartments,
      ...fetchedOnly.slice(0, fetchedSlots),
    ];

    return orderBy(finalDepartments, 'name', 'asc');
  }, [fetchedDepartments, preloadedDepartments]);

  const filterOptions = (options: DepartmentV2[]) =>
    options.filter((option) => {
      const isWhitelisted =
        specificDepartments?.length && specificDepartments?.includes(option.id);
      const isNotBlacklisted =
        notAvailableDepartments?.length &&
        !notAvailableDepartments?.includes(option.id);

      return isWhitelisted || isNotBlacklisted;
    });

  const departmentOptions = useMemo(
    () =>
      specificDepartments?.length || notAvailableDepartments?.length
        ? filterOptions(allDepartments)
        : allDepartments,
    [specificDepartments, notAvailableDepartments, allDepartments],
  );

  return (
    <Box w={selectWidths[width]}>
      <EdsDepartmentSelect
        {...rest}
        // disabling until we have BE support
        enableSelectAll={false}
        value={value}
        isMulti={isMulti}
        name={
          name ??
          (isMulti
            ? intl.formatMessage({
                id: 'DepartmentSelect.multiSelectName',
                defaultMessage: 'Multi-Department Select',
                description: 'Default name for multi-department select',
              })
            : intl.formatMessage({
                id: 'DepartmentSelect.singleSelectName',
                defaultMessage: 'Single Department Select',
                description: 'Default name for single department select',
              }))
        }
        placeholder={
          isMulti
            ? intl.formatMessage({
                id: 'DepartmentSelect.multiSelectPlaceholder',
                defaultMessage: 'Specify one or more departments',
                description: 'Placeholder for multi-department select',
              })
            : intl.formatMessage({
                id: 'DepartmentSelect.singleSelectPlaceholder',
                defaultMessage: 'Specify a department',
                description: 'Placeholder for single department select',
              })
        }
        departments={departmentOptions}
        error={coerceRtkqError(fetchDepartmentsError) || error}
        isLoading={
          isFetchingDepartments || isFetchingDepartmentsTree || !currentClientId
        }
        width="100%"
        onSearch={(query: string) =>
          debouncedFindDepartments({
            query,
            pageSize: PAGE_SIZE,
            clientId: currentClientId,
          })
        }
        onChange={onChange}
      />
    </Box>
  );
};

export default withClient(DepartmentSelect);
